import { useMemo } from "react";
import { useStore } from "ra-core";

import type { DealStage, LabeledValue, NoteStatus } from "../types";
import { defaultConfiguration } from "./defaultConfiguration";
import {
  attachmentPath,
  useAttachmentUrl,
} from "../providers/commons/attachments";

export const CONFIGURATION_STORE_KEY = "app.configuration";

export interface ConfigurationContextValue {
  companySectors: LabeledValue[];
  currency: string;
  dealCategories: LabeledValue[];
  dealPipelineStatuses: string[];
  dealStages: DealStage[];
  noteStatuses: NoteStatus[];
  taskTypes: LabeledValue[];
  title: string;
  darkModeLogo: string;
  lightModeLogo: string;
  googleWorkplaceDomain?: string;
  disableEmailPasswordAuthentication?: boolean;
}

export const useConfigurationContext = () => {
  const [config] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
    defaultConfiguration,
  );
  // Merge with defaults so that missing fields in stored config
  // fall back to default values (e.g. when new settings are added)
  return useMemo(() => ({ ...defaultConfiguration, ...config }), [config]);
};

export const useBranding = (publicOnly = false) => {
  const config = useConfigurationContext();
  const darkAttachment = { src: config.darkModeLogo };
  const lightAttachment = { src: config.lightModeLogo };
  const darkModeLogo = useAttachmentUrl(
    publicOnly && attachmentPath(darkAttachment) ? null : darkAttachment,
  );
  const lightModeLogo = useAttachmentUrl(
    publicOnly && attachmentPath(lightAttachment) ? null : lightAttachment,
  );
  return {
    ...config,
    darkModeLogo: darkModeLogo || defaultConfiguration.darkModeLogo,
    lightModeLogo: lightModeLogo || defaultConfiguration.lightModeLogo,
  };
};

export const useConfigurationUpdater = () => {
  const [, setConfig] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
  );
  return setConfig;
};
